import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MenuProvider } from "@/context/MenuContext";
import { AccountDrawer } from "@/components/jk/AccountDrawer";
import { FullScreenLoader } from "@/components/jk/kit";

import EmailOtpEntry from "@/screens/EmailOtpEntry";
import OtpVerification from "@/screens/OtpVerification";
import OnboardingProfile from "@/screens/OnboardingProfile";
import OnboardingServices from "@/screens/OnboardingServices";
import OnboardingAvailability from "@/screens/OnboardingAvailability";
import OnboardingPayout from "@/screens/OnboardingPayout";
import OnboardingComplete from "@/screens/OnboardingComplete";
import Dashboard from "@/screens/Dashboard";
import ClientList from "@/screens/ClientList";
import ClientProfile from "@/screens/ClientProfile";
import ClientBrief from "@/screens/ClientBrief";
import Calendar from "@/screens/Calendar";
import Kyc from "@/screens/Kyc";
import Paywall from "@/screens/Paywall";
import PublicBooking from "@/screens/PublicBooking";
import ClientIntake from "@/screens/ClientIntake";
import Payment from "@/screens/Payment";
import BookingConfirmed from "@/screens/BookingConfirmed";
import ReviewRequest from "@/screens/ReviewRequest";
import ProductBuy from "@/screens/ProductBuy";
import ProductOrderDone from "@/screens/ProductOrderDone";
import AdminApp from "@/admin/AdminApp";

const RequireAuth = ({ children, requireOnboarding }) => {
  const { astrologer, onboardingComplete, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!astrologer) return <Navigate to="/signin" replace state={{ from: location }} />;
  if (requireOnboarding && !onboardingComplete) return <Navigate to="/onboarding/profile" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Admin dashboard (separate auth namespace) */}
      <Route path="/admin/*" element={<AdminApp />} />

      {/* Public / auth */}
      <Route path="/" element={<Navigate to="/signin" replace />} />
      <Route path="/signin" element={<EmailOtpEntry />} />
      <Route path="/verify" element={<OtpVerification />} />

      {/* Onboarding (auth, onboarding may be incomplete) */}
      <Route path="/onboarding/profile" element={<RequireAuth><OnboardingProfile /></RequireAuth>} />
      <Route path="/onboarding/services" element={<RequireAuth><OnboardingServices /></RequireAuth>} />
      <Route path="/onboarding/availability" element={<RequireAuth><OnboardingAvailability /></RequireAuth>} />
      <Route path="/onboarding/payout" element={<RequireAuth><OnboardingPayout /></RequireAuth>} />
      <Route path="/onboarding/complete" element={<RequireAuth><OnboardingComplete /></RequireAuth>} />

      {/* App (auth + onboarding complete) */}
      <Route path="/dashboard" element={<RequireAuth requireOnboarding><Dashboard /></RequireAuth>} />
      <Route path="/clients" element={<RequireAuth requireOnboarding><ClientList /></RequireAuth>} />
      <Route path="/clients/:id" element={<RequireAuth requireOnboarding><ClientProfile /></RequireAuth>} />
      <Route path="/brief/:id" element={<RequireAuth requireOnboarding><ClientBrief /></RequireAuth>} />
      <Route path="/calendar" element={<RequireAuth requireOnboarding><Calendar /></RequireAuth>} />
      <Route path="/edit/profile" element={<RequireAuth requireOnboarding><OnboardingProfile /></RequireAuth>} />
      <Route path="/edit/services" element={<RequireAuth requireOnboarding><OnboardingServices /></RequireAuth>} />
      <Route path="/edit/payout" element={<RequireAuth requireOnboarding><OnboardingPayout /></RequireAuth>} />
      <Route path="/kyc" element={<RequireAuth requireOnboarding><Kyc /></RequireAuth>} />
      <Route path="/paywall" element={<RequireAuth requireOnboarding><Paywall /></RequireAuth>} />

      {/* Public booking flow */}
      <Route path="/booking/:id/done" element={<BookingConfirmed />} />
      <Route path="/review/:id" element={<ReviewRequest />} />
      <Route path="/:slug/intake" element={<ClientIntake />} />
      <Route path="/:slug/payment" element={<Payment />} />
      <Route path="/:slug" element={<PublicBooking />} />

      {/* Product commerce */}
      <Route path="/products/:productId/buy" element={<ProductBuy />} />
      <Route path="/product-order/:orderId/done" element={<ProductOrderDone />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <MenuProvider>
            <AppRoutes />
            <AccountDrawer />
          </MenuProvider>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
